import React, { useState } from 'react';

/**
 * Internal Audit Guidance Component
 * Provides specialized guidance for internal audit tasks
 */
function InternalAuditGuidance({ task, fileInputRef, uploading, files, onFileUpload }) {
  const [showUpload, setShowUpload] = useState(false);
  const hasFiles = files && files.length > 0;

  const auditSteps = [
    { step: '1. Planlegg', description: 'Bestem hva som skal revideres, når og av hvem', duration: '1-2 timer' },
    { step: '2. Forbered', description: 'Lag sjekkliste basert på krav og prosedyrer', duration: '2-4 timer' },
    { step: '3. Gjennomfør', description: 'Intervju, observasjon, dokumentgjennomgang', duration: '4-8 timer' },
    { step: '4. Rapporter', description: 'Dokumenter funn og forbedringsmuligheter', duration: '1-2 timer' },
    { step: '5. Følg opp', description: 'Korrigerende tiltak for avvik', duration: 'Løpende' },
  ];

  const checklistItems = [
    'Er prosedyrer oppdaterte og kjent for ansatte?',
    'Blir registreringer ført som de skal?',
    'Er utstyr kontrollert og i orden?',
    'Følges HMS-rutiner i praksis?',
    'Er kompetansekrav oppfylt?',
    'Er avvik fra sist gang lukket?',
  ];

  return (
    <div data-testid="internal-audit-guidance">
      {/* Why this is important */}
      <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-indigo-900 mb-1">Sjekk at systemet fungerer</p>
            <p className="text-sm text-indigo-800">
              Intern revisjon avdekker svakheter før eksterne revisorer eller myndigheter finner dem. 
              Det er en læringsmulighet som forbedrer hele organisasjonen.
            </p>
          </div>
        </div>
      </div>

      {/* Audit process steps */}
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
        Revisjonsprosessen
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
        <div className="space-y-3">
          {auditSteps.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm">
                {idx + 1}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{item.step.split('. ')[1]}</p>
                <p className="text-xs text-gray-500">{item.description}</p>
              </div>
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                {item.duration}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick checklist */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-gray-900 mb-3">Eksempel på sjekkliste</h4>
        <div className="space-y-2">
          {checklistItems.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="w-4 h-4 mt-0.5 border border-gray-300 rounded bg-white flex-shrink-0"></div>
              <span className="text-sm text-gray-700">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-green-900 mb-2">Tips for god revisjon</h4>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• Revisor bør ikke revidere eget arbeid</li>
          <li>• Still åpne spørsmål ("Vis meg hvordan..." / "Forklar...")</li>
          <li>• Fokuser på forbedringsmuligheter, ikke feilfinning</li>
          <li>• Minimum årlig revisjon for ISO-sertifisering</li>
        </ul>
      </div>

      {/* Upload option */}
      <div className="border border-gray-200 rounded-xl p-4">
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="w-full flex items-center gap-3 text-left"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <span className="font-medium text-gray-900">Last opp revisjonsrapport eller plan</span>
            <p className="text-sm text-gray-500 mt-0.5">
              Dokumenter revisjonen
            </p>
          </div>
          <svg className={`w-5 h-5 text-gray-400 transition-transform ${showUpload ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showUpload && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Laster opp...' : 'Last opp dokument'}
            </button>
            {hasFiles && (
              <p className="text-xs text-green-600 mt-2 text-center">
                ✓ {files.length} dokument(er) lastet opp
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default InternalAuditGuidance;
